// src/pages/Radar.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import ReferrerZipHeatmap from "../components/ReferrerZipHeatmap";

export default function Radar() {
  const [referrers, setReferrers] = useState([]);
  const [searchZip, setSearchZip] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchSort, setSearchSort] = useState("activity");
  const [selectedZipData, setSelectedZipData] = useState(null);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedProviderTypes, setSelectedProviderTypes] = useState([]);
  const [radarMode, setRadarMode] = useState("opportunity");
  const [entryPlan, setEntryPlan] = useState(null);
  const [entryPlanLoading, setEntryPlanLoading] = useState(false);
  const [entryPlanError, setEntryPlanError] = useState("");
  const [entryPlanSelected, setEntryPlanSelected] = useState([]);
  const [entryPlanCreated, setEntryPlanCreated] = useState(null);
  const [zipInsights, setZipInsights] = useState(null);
  const [zipInsightsLoading, setZipInsightsLoading] = useState(false);
  const [zipInsightsError, setZipInsightsError] = useState("");
  const [zipRelationshipFilter, setZipRelationshipFilter] = useState("all");
  const [zipSpecialtyFilter, setZipSpecialtyFilter] = useState("all");
  const [zipSortBy, setZipSortBy] = useState("best_targets");
  const [zipCommercialOnly, setZipCommercialOnly] = useState(false);
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [activePracticeAreaId, setActivePracticeAreaId] = useState("");
  const [outreachPlans, setOutreachPlans] = useState([]);
  const [outreachModal, setOutreachModal] = useState(null); // { referrerId?, npi?, name }
  const [outreachPlanId, setOutreachPlanId] = useState("");
  const [outreachCategory, setOutreachCategory] = useState("expand");
  const [outreachError, setOutreachError] = useState("");
  const [outreachSubmitting, setOutreachSubmitting] = useState(false);
  const [me, setMe] = useState(null);
  const pageSize = 50;

  // Who is this? Signup specialty + state drive the suggestions below.
  useEffect(() => {
    const token = localStorage.getItem("token");
    axios
      .get(`${process.env.REACT_APP_API}/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setMe(res.data))
      .catch(() => {});
  }, []);

  // Provider types available in the radar dataset. Every key here MUST be a
  // taxonomy code the dataset was built with (npi_service.PROVIDER_TAXONOMY_CODES)
  // — a code outside that list matches zero providers and blanks the heatmap.
  const PROVIDER_TYPES = [
    // Primary Care
    { key: "207Q00000X", label: "Family Medicine", category: "Primary Care" },
    { key: "207R00000X", label: "Internal Medicine", category: "Primary Care" },
    { key: "208D00000X", label: "General Practice", category: "Primary Care" },
    { key: "208000000X", label: "Pediatrics", category: "Primary Care" },
    { key: "208M00000X", label: "Hospitalist", category: "Primary Care" },

    // Advanced Practice
    { key: "363L00000X", label: "Nurse Practitioner", category: "Advanced Practice" },
    { key: "363A00000X", label: "Physician Assistant", category: "Advanced Practice" },

    // Physical/Rehab
    { key: "225100000X", label: "Physical Therapist", category: "Rehab" },
    { key: "225X00000X", label: "Occupational Therapist", category: "Rehab" },
    { key: "111N00000X", label: "Chiropractor", category: "Rehab" },
    { key: "111NS0005X", label: "Sports Chiropractor", category: "Rehab" },

    // Pain
    { key: "208VP0014X", label: "Pain Medicine", category: "Pain" },
    { key: "208VP0000X", label: "Interventional Pain", category: "Pain" },
    { key: "207LP2900X", label: "Anesthesiology (Pain)", category: "Pain" },

    // Neuro
    { key: "2084N0400X", label: "Neurology", category: "Neuro" },
    { key: "207T00000X", label: "Neurological Surgery", category: "Neuro" },

    // Surgery
    { key: "207X00000X", label: "Orthopedic Surgery", category: "Surgery" },
    { key: "207XS0114X", label: "Spine Surgery", category: "Surgery" },
    { key: "207XX0004X", label: "Sports Medicine (Ortho)", category: "Surgery" },
    { key: "208600000X", label: "General Surgery", category: "Surgery" },
    { key: "208200000X", label: "Plastic Surgery", category: "Surgery" },
    { key: "208G00000X", label: "Cardiothoracic / Vascular", category: "Surgery" },

    // Cardiology
    { key: "207RC0000X", label: "Cardiovascular Disease", category: "Cardiology" },
    { key: "207RI0200X", label: "Interventional Cardiology", category: "Cardiology" },
    { key: "207RC0001X", label: "Electrophysiology", category: "Cardiology" },

    // Emergency
    { key: "207P00000X", label: "Emergency Medicine", category: "Emergency" },

    // Mental Health
    { key: "2084P0800X", label: "Psychiatry", category: "Mental Health" },
    { key: "2084P0301X", label: "Addiction Medicine", category: "Mental Health" },

    // Radiology
    { key: "2085R0202X", label: "Diagnostic Radiology", category: "Radiology" },
    { key: "2085R0001X", label: "Interventional Radiology", category: "Radiology" },

    // Dental
    { key: "122300000X", label: "Dentist", category: "Dental" },
    { key: "1223G0001X", label: "General Dentist", category: "Dental" },
    { key: "1223S0112X", label: "Oral Surgeon (OMFS)", category: "Dental" },
    { key: "1223X0400X", label: "Orthodontist", category: "Dental" },
    { key: "1223E0200X", label: "Endodontist", category: "Dental" },
    { key: "1223P0221X", label: "Periodontist", category: "Dental" },

    // Podiatry
    { key: "213E00000X", label: "Podiatrist", category: "Podiatry" },
  ];

  // One-click referrer mixes by surgeon specialty — who actually sends you
  // the patients you want. Keys must exist in PROVIDER_TYPES.
  const REFERRER_MIX_PRESETS = [
    {
      label: "Spine Surgery",
      hint: "PCP, pain, PT, chiro, neuro",
      keys: [
        "207Q00000X", "207R00000X", "208D00000X", "363L00000X", "363A00000X",
        "225100000X", "111N00000X", "208VP0014X", "208VP0000X", "207LP2900X",
        "2084N0400X", "207P00000X",
      ],
    },
    {
      label: "Cardiothoracic",
      hint: "cardiology + medicine",
      keys: [
        "207RC0000X", "207RI0200X", "207RC0001X", "207R00000X", "207Q00000X",
        "208D00000X", "207P00000X", "208M00000X",
      ],
    },
    {
      label: "OMFS / Oral Surgery",
      hint: "dentists + primary care",
      keys: [
        "122300000X", "1223G0001X", "1223X0400X", "1223E0200X", "1223P0221X",
        "207Q00000X", "208D00000X", "207P00000X",
      ],
    },
    {
      label: "Neurosurgery (Cranial)",
      hint: "neurology, EM, radiology",
      keys: [
        "2084N0400X", "207P00000X", "2085R0202X", "2085R0001X", "207R00000X",
        "207Q00000X", "208VP0014X",
      ],
    },
    {
      label: "Orthopedics (General)",
      hint: "PCP, PT, chiro, sports, podiatry",
      keys: [
        "207Q00000X", "207R00000X", "208D00000X", "363L00000X", "363A00000X",
        "225100000X", "225X00000X", "111N00000X", "111NS0005X", "208VP0014X",
        "207P00000X", "213E00000X",
      ],
    },
  ];

  // Signup specialty -> the referrer mix we suggest on the radar.
  const SPECIALTY_SUGGESTIONS = [
    { re: /spine/i, preset: "Spine Surgery" },
    { re: /neurosurg/i, preset: "Neurosurgery (Cranial)" },
    { re: /orthop|sports med/i, preset: "Orthopedics (General)" },
    { re: /cardio|thoracic|vascular/i, preset: "Cardiothoracic" },
    { re: /oral|maxillofacial|dent/i, preset: "OMFS / Oral Surgery" },
    { re: /pain|physical medicine|rehab/i, preset: "Spine Surgery" },
  ];
  // Everyone else gets the referral base every specialist shares: primary
  // care, advanced practice, and the ED.
  const GENERIC_REFERRAL_BASE = [
    "207Q00000X", "207R00000X", "208D00000X", "363L00000X", "363A00000X", "207P00000X",
  ];

  const mySpecialty = me?.user?.specialty || "";
  const suggestedMatch = mySpecialty
    ? SPECIALTY_SUGGESTIONS.find((s) => s.re.test(mySpecialty))
    : null;
  const suggestedPreset = suggestedMatch
    ? REFERRER_MIX_PRESETS.find((p) => p.label === suggestedMatch.preset)
    : null;
  const suggestedKeys = suggestedPreset ? suggestedPreset.keys : mySpecialty ? GENERIC_REFERRAL_BASE : [];

  // All 50 US states
  const ALL_STATES = [
    { code: "AL", name: "Alabama" },
    { code: "AK", name: "Alaska" },
    { code: "AZ", name: "Arizona" },
    { code: "AR", name: "Arkansas" },
    { code: "CA", name: "California" },
    { code: "CO", name: "Colorado" },
    { code: "CT", name: "Connecticut" },
    { code: "DE", name: "Delaware" },
    { code: "FL", name: "Florida" },
    { code: "GA", name: "Georgia" },
    { code: "HI", name: "Hawaii" },
    { code: "ID", name: "Idaho" },
    { code: "IL", name: "Illinois" },
    { code: "IN", name: "Indiana" },
    { code: "IA", name: "Iowa" },
    { code: "KS", name: "Kansas" },
    { code: "KY", name: "Kentucky" },
    { code: "LA", name: "Louisiana" },
    { code: "ME", name: "Maine" },
    { code: "MD", name: "Maryland" },
    { code: "MA", name: "Massachusetts" },
    { code: "MI", name: "Michigan" },
    { code: "MN", name: "Minnesota" },
    { code: "MS", name: "Mississippi" },
    { code: "MO", name: "Missouri" },
    { code: "MT", name: "Montana" },
    { code: "NE", name: "Nebraska" },
    { code: "NV", name: "Nevada" },
    { code: "NH", name: "New Hampshire" },
    { code: "NJ", name: "New Jersey" },
    { code: "NM", name: "New Mexico" },
    { code: "NY", name: "New York" },
    { code: "NC", name: "North Carolina" },
    { code: "ND", name: "North Dakota" },
    { code: "OH", name: "Ohio" },
    { code: "OK", name: "Oklahoma" },
    { code: "OR", name: "Oregon" },
    { code: "PA", name: "Pennsylvania" },
    { code: "RI", name: "Rhode Island" },
    { code: "SC", name: "South Carolina" },
    { code: "SD", name: "South Dakota" },
    { code: "TN", name: "Tennessee" },
    { code: "TX", name: "Texas" },
    { code: "UT", name: "Utah" },
    { code: "VT", name: "Vermont" },
    { code: "VA", name: "Virginia" },
    { code: "WA", name: "Washington" },
    { code: "WV", name: "West Virginia" },
    { code: "WI", name: "Wisconsin" },
    { code: "WY", name: "Wyoming" },
  ];

  // Function to add a state
  const addState = (stateCode) => {
    if (stateCode && !selectedStates.includes(stateCode)) {
      setSelectedStates([...selectedStates, stateCode]);
    }
  };

  // Function to remove a state
  const removeState = (stateCode) => {
    setSelectedStates(selectedStates.filter((s) => s !== stateCode));
  };

  // Get available states for dropdowns
  const getAvailableStates = () => {
    return ALL_STATES.filter((state) => !selectedStates.includes(state.code));
  };

  const formatPhone = (raw) => {
    const digits = String(raw).replace(/\D/g, "").slice(0, 10);
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return raw;
  };

  const logReferrer = async (referrerId, status) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API}/my-referrers`,
        { referrer_id: referrerId, status },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      // Optimistic update: reflect the new relationship without a refetch
      setZipInsights((prev) => {
        if (!prev) return prev;
        const label = status === "active" ? "Referring to you" : "Prospect";
        return {
          ...prev,
          providers: (prev.providers || []).map((p) =>
            p.id === referrerId ? { ...p, link_status: status, relationship_status: label } : p
          ),
        };
      });
    } catch (err) {
      console.error("Failed to log referrer:", err);
      alert(err.response?.data?.error || "Failed to log referrer");
    }
  };

  const openOutreachModal = (modalData) => {
    setOutreachError("");
    setOutreachPlanId("");
    setOutreachCategory("expand");
    setOutreachModal(modalData);
  };

  const closeOutreachModal = () => {
    setOutreachModal(null);
    setOutreachError("");
    setOutreachSubmitting(false);
    setOutreachPlanId("");
    setOutreachCategory("expand");
  };

  const resolveOutreachReferrerId = async (modalData) => {
    if (modalData?.referrerId) {
      return modalData.referrerId;
    }
    if (!modalData?.npi) {
      throw new Error("This provider cannot be added because no NPI is available.");
    }

    const token = localStorage.getItem("token");
    const res = await axios.get(
      `${process.env.REACT_APP_API}/npi/lookup/${modalData.npi}`,
      {
        params: { cache: true },
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const resolvedId = res.data?.provider?.id;
    if (!resolvedId) {
      throw new Error("Provider could not be prepared for outreach.");
    }
    return resolvedId;
  };

  useEffect(() => {
    const zip = selectedZipData?.zip;
    if (!zip) {
      setZipInsights(null);
      setZipInsightsError("");
      return;
    }

    const fetchZipInsights = async () => {
      try {
        setZipInsightsLoading(true);
        setZipInsightsError("");
        const token = localStorage.getItem("token");
        const params = {
          zip_code: zip,
          sort_by: zipSortBy,
          ...(zipCommercialOnly ? { payer: "commercial" } : {}),
        };

        if (selectedProviderTypes.length > 0) {
          params.provider_types = selectedProviderTypes.join(",");
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API}/radar/zip-insights`,
          {
            params,
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setZipInsights(response.data);
      } catch (error) {
        console.error("Failed to load ZIP insights:", error);
        setZipInsights(null);
        setZipInsightsError(
          error.response?.data?.error || "Failed to load ZIP insight data."
        );
      } finally {
        setZipInsightsLoading(false);
      }
    };

    fetchZipInsights();
  }, [selectedZipData?.zip, selectedProviderTypes, zipSortBy, zipCommercialOnly]);

  useEffect(() => {
    setZipRelationshipFilter("all");
    setZipSpecialtyFilter("all");
  }, [selectedZipData?.zip]);

  const filteredZipProviders = (zipInsights?.providers || []).filter((provider) => {
    if (
      zipRelationshipFilter === "referring" &&
      provider.relationship_status !== "Referring to you"
    ) {
      return false;
    }

    if (
      zipRelationshipFilter === "untapped" &&
      provider.relationship_status !== "Untapped"
    ) {
      return false;
    }

    if (zipRelationshipFilter === "whales" && provider.activity_tier !== "Top Referrer") {
      return false;
    }

    if (
      zipSpecialtyFilter !== "all" &&
      (provider.specialty || "Unknown") !== zipSpecialtyFilter
    ) {
      return false;
    }

    return true;
  });

  // Search providers - uses NPI API for real-time access to ALL providers
  const searchProviders = async (zipOverride) => {
    try {
      setLoading(true);

      // Build search parameters
      const params = {};

      // Handle multiple ZIP codes (comma-separated)
      // Guard: onClick handlers pass the click event — only accept strings
      const effectiveZip = typeof zipOverride === "string" ? zipOverride : searchZip;
      if (effectiveZip) {
        params.postal_code = effectiveZip.trim();
      }

      // Add selected states if any
      if (selectedStates.length > 0) {
        params.state = selectedStates.join(',');
      }

      // Add selected provider types if any
      if (selectedProviderTypes.length > 0) {
        params.provider_types = selectedProviderTypes.join(',');
      }

      // Search NPI API directly for ALL providers
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${process.env.REACT_APP_API}/npi/search`,
        { 
          params,
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Ensure response is an array before setting state
      if (Array.isArray(res.data)) {
        setReferrers(res.data);
      } else {
        console.error("NPI search response is not an array:", res.data);
        setReferrers([]);
      }
      setCurrentPage(1);
    } catch (err) {
      console.error("Failed to search providers:", err);
      setReferrers([]); // Reset to empty array on error
      alert("Failed to search providers. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const buildEntryPlan = async () => {
    setEntryPlanLoading(true);
    setEntryPlanError("");
    setEntryPlanCreated(null);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      params.set("practice_area_id", activePracticeArea.id);
      if (selectedProviderTypes.length > 0) {
        params.set("provider_types", selectedProviderTypes.join(","));
      }
      params.set("limit", "25");
      const res = await fetch(`${process.env.REACT_APP_API}/radar/entry-plan?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to build entry plan");
      setEntryPlan(data);
      setEntryPlanSelected(data.targets.slice(0, 10).map((target) => target.referrer_id));
    } catch (err) {
      setEntryPlanError(err.message);
    } finally {
      setEntryPlanLoading(false);
    }
  };

  const toggleEntryTarget = (referrerId) => {
    setEntryPlanSelected((prev) =>
      prev.includes(referrerId) ? prev.filter((id) => id !== referrerId) : [...prev, referrerId]
    );
  };

  const createOutreachFromEntryPlan = async () => {
    if (entryPlanSelected.length === 0) return;
    setEntryPlanError("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_API}/radar/entry-plan/outreach`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: `Entry plan — ${entryPlan?.area_name || "selected area"}`,
          referrer_ids: entryPlanSelected,
          category: "expand",
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create outreach plan");
      setEntryPlanCreated(data);
    } catch (err) {
      setEntryPlanError(err.message);
    }
  };

  const toggleProviderType = (typeKey) => {
    if (selectedProviderTypes.includes(typeKey)) {
      setSelectedProviderTypes(selectedProviderTypes.filter((t) => t !== typeKey));
    } else {
      setSelectedProviderTypes([...selectedProviderTypes, typeKey]);
    }
  };

  // Load outreach plans
  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${process.env.REACT_APP_API}/outreach/plans`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : [])
      .then(setOutreachPlans)
      .catch(() => {});
  }, []);

  const addToOutreach = async () => {
    if (!outreachPlanId || !outreachModal) return;
    try {
      setOutreachSubmitting(true);
      setOutreachError("");
      const token = localStorage.getItem("token");
      const referrerId = await resolveOutreachReferrerId(outreachModal);
      await axios.post(
        `${process.env.REACT_APP_API}/outreach/plans/${outreachPlanId}/targets`,
        { referrer_id: referrerId, category: outreachCategory },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Added to outreach plan!");
      closeOutreachModal();
    } catch (err) {
      setOutreachError(err.response?.data?.error || err.message || "Failed to add");
    } finally {
      setOutreachSubmitting(false);
    }
  };

  // Load practice areas for filtering
  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${process.env.REACT_APP_API}/practice-areas`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : [])
      .then(setPracticeAreas)
      .catch(() => {});
  }, []);

  // Filter referrers by active practice area
  const activePracticeArea = practiceAreas.find((a) => a.id === parseInt(activePracticeAreaId, 10));
  const activePracticeAreaZipCodes = (activePracticeArea?.zip_codes || []).map((zip) => String(zip).slice(0, 5));
  const activePracticeAreaZipSet = activePracticeArea
    ? new Set(activePracticeAreaZipCodes)
    : null;
  const filteredReferrers = activePracticeArea
    ? referrers.filter((r) => activePracticeAreaZipSet.has(String(r.zip_code || "").slice(0, 5)))
    : referrers;

  const sortedReferrers = [...filteredReferrers].sort((a, b) => {
    const activityOf = (r) => (r.activity_score == null ? -1 : r.activity_score);
    switch (searchSort) {
      case "specialty":
        return (a.specialty || "").localeCompare(b.specialty || "") || activityOf(b) - activityOf(a);
      case "location":
        return (a.city || "").localeCompare(b.city || "") ||
          (a.zip_code || "").localeCompare(b.zip_code || "") ||
          activityOf(b) - activityOf(a);
      case "name":
        return (a.name || "").localeCompare(b.name || "");
      case "private_payer": {
        const pp = (r) => (r.private_payer_score == null ? -1 : r.private_payer_score);
        return pp(b) - pp(a) || activityOf(b) - activityOf(a);
      }
      case "activity":
      default:
        return activityOf(b) - activityOf(a) ||
          (b.market_referral_volume_12m || 0) - (a.market_referral_volume_12m || 0) ||
          (a.name || "").localeCompare(b.name || "");
    }
  });

  const totalPages = Math.ceil(sortedReferrers.length / pageSize);
  const currentReferrers = sortedReferrers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const payerBadge = (provider) => {
    if (!provider || provider.private_payer_tier == null) {
      return <span className="text-xs text-gray-400">—</span>;
    }
    const classes = {
      "Commercial-leaning": "bg-emerald-100 text-emerald-800 border-emerald-300",
      "Mixed": "bg-yellow-100 text-yellow-800 border-yellow-300",
      "Medicare/Medicaid-heavy": "bg-orange-100 text-orange-800 border-orange-300",
    }[provider.private_payer_tier] || "bg-gray-100 text-gray-600 border-gray-300";
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-medium border rounded-full px-2 py-0.5 ${classes}`}>
        {provider.private_payer_tier}
        {provider.private_payer_score != null && (
          <span className="opacity-70">({Math.round(provider.private_payer_score)})</span>
        )}
      </span>
    );
  };

  const tierBadgeClass = (tier) => {
    switch (tier) {
      case "Top Referrer": return "bg-purple-100 text-purple-800 border-purple-300";
      case "High Activity": return "bg-green-100 text-green-800 border-green-300";
      case "Moderate": return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "Emerging": return "bg-gray-100 text-gray-600 border-gray-300";
      default: return "bg-gray-50 text-gray-400 border-gray-200";
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Referral Radar</h2>

      {/* Practice Area Filter */}
      {practiceAreas.length > 0 && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <label className="block text-sm font-medium text-green-800 mb-1">
            Filter by Practice Area:
          </label>
          <select
            className="border rounded px-3 py-2 text-sm w-64"
            value={activePracticeAreaId}
            onChange={(e) => {
              setActivePracticeAreaId(e.target.value);
              setSelectedZipData(null);
              setCurrentPage(1);
            }}
          >
            <option value="">All Providers (no filter)</option>
            {practiceAreas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({(a.zip_codes || []).length} ZIPs)
              </option>
            ))}
          </select>
        </div>
      )}

      {/* State Selection */}
      <div className="mb-4">
        <label className="block font-medium mb-1">
          1 · What location?
        </label>
        <p className="text-sm text-gray-500 mb-2">Add the state(s) you want to analyze.</p>
        {(() => {
          const myStateCode = ALL_STATES.find(
            (s) => s.name.toLowerCase() === String(me?.user?.state || "").toLowerCase()
          )?.code;
          return myStateCode && !selectedStates.includes(myStateCode) ? (
            <button
              onClick={() => addState(myStateCode)}
              className="mb-3 px-3 py-1 rounded-full text-sm border border-green-300 bg-green-50 text-green-800 hover:bg-green-100"
            >
              + Add my state ({me.user.state})
            </button>
          ) : null;
        })()}
        {activePracticeArea && (
          <p className="text-sm text-green-700 mb-3">
            Heatmap focus is currently using <strong>{activePracticeArea.name}</strong>. Clear the practice-area filter to go back to state-wide analysis.
          </p>
        )}

        {/* Currently Selected States */}
        {selectedStates.length > 0 && (
          <div className="mb-3">
            <p className="text-sm text-gray-600 mb-2">Currently analyzing:</p>
            <div className="flex flex-wrap gap-2">
              {selectedStates.map((stateCode) => {
                const state = ALL_STATES.find((s) => s.code === stateCode);
                return (
                  <span
                    key={stateCode}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                  >
                    {state?.name} ({stateCode})
                    <button
                      onClick={() => removeState(stateCode)}
                      className="ml-1 text-blue-600 hover:text-blue-800 font-bold focus:outline-none bg-transparent border-none p-1"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Add State Dropdown */}
        {getAvailableStates().length > 0 && (
          <div className="flex gap-2">
            <select
              className="border rounded px-3 py-2 flex-1"
              onChange={(e) => {
                if (e.target.value) {
                  addState(e.target.value);
                  e.target.value = "";
                }
              }}
              defaultValue=""
            >
              <option value="" disabled>
                Select a state to add...
              </option>
              {getAvailableStates().map((state) => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Provider Type Selection */}
      <div className="mb-4">
        <label className="block font-medium mb-1">
          2 · What provider types send you patients?
        </label>
        <p className="text-sm text-gray-500 mb-2">
          Pick the referrer types to focus the map on — or leave empty to see every provider.
        </p>

        {/* Suggested for this user's signup specialty */}
        {suggestedKeys.length > 0 && (
          <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-green-900">
                Suggested for <strong>{mySpecialty}</strong>:
              </span>
              <button
                onClick={() => setSelectedProviderTypes(suggestedKeys)}
                className="px-3 py-1 rounded-full text-sm bg-green-700 text-white hover:bg-green-800"
              >
                Use suggested mix ({suggestedKeys.length})
              </button>
            </div>
            <p className="mt-1.5 text-xs text-green-800">
              {suggestedKeys
                .map((k) => PROVIDER_TYPES.find((t) => t.key === k)?.label)
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
        )}

        {/* Referrer mix presets by surgeon specialty */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-600">Referrer mixes:</span>
          {REFERRER_MIX_PRESETS.map((preset) => (
            <button
              key={preset.label}
              onClick={() => setSelectedProviderTypes(preset.keys)}
              title={preset.hint}
              className="px-3 py-1 rounded-full text-sm border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100"
            >
              {preset.label}
            </button>
          ))}
          {selectedProviderTypes.length > 0 && (
            <button
              onClick={() => setSelectedProviderTypes([])}
              className="px-3 py-1 rounded-full text-sm border border-gray-300 bg-gray-50 text-gray-600 hover:bg-gray-100"
            >
              Clear
            </button>
          )}
        </div>

        {/* Currently Selected Provider Types */}
        {selectedProviderTypes.length > 0 && (
          <div className="mb-3">
            <p className="text-sm text-gray-600 mb-2">Selected types:</p>
            <div className="flex flex-wrap gap-2">
              {selectedProviderTypes.map((typeKey) => {
                const type = PROVIDER_TYPES.find((t) => t.key === typeKey);
                return (
                  <span
                    key={typeKey}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                  >
                    {type?.label}
                    <button
                      onClick={() => toggleProviderType(typeKey)}
                      className="ml-1 text-green-600 hover:text-green-800 font-bold focus:outline-none bg-transparent border-none p-1"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Full list, A–Z */}
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
          All provider types (A–Z)
        </div>
        <div className="flex flex-wrap gap-2">
          {[...PROVIDER_TYPES]
            .sort((a, b) => a.label.localeCompare(b.label))
            .map((type) => (
              <button
                key={type.key}
                onClick={() => toggleProviderType(type.key)}
                className={`px-3 py-1.5 rounded-full border text-sm ${
                  selectedProviderTypes.includes(type.key)
                    ? "bg-green-600 text-white border-green-700"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {type.label}
              </button>
            ))}
        </div>
      </div>

      {/* Heatmap */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold">Market Intelligence Heatmap</h3>

          {/* Radar Mode Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setRadarMode('opportunity')}
              className={`px-4 py-2 rounded text-sm font-medium ${
                radarMode === 'opportunity'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Opportunity
            </button>
            <button
              onClick={() => setRadarMode('value')}
              className={`px-4 py-2 rounded text-sm font-medium ${
                radarMode === 'value'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Value
            </button>
            <button
              onClick={() => setRadarMode('penetration')}
              className={`px-4 py-2 rounded text-sm font-medium ${
                radarMode === 'penetration'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Penetrance
            </button>
          </div>
        </div>

        <div
          style={{
            height: "500px",
            width: "100%",
            position: "relative",
            overflow: "hidden",
            border: "1px solid #ccc",
            borderRadius: "8px",
          }}
        >
          <ReferrerZipHeatmap
            selectedStates={activePracticeArea ? [] : selectedStates}
            selectedZipCodes={activePracticeAreaZipCodes}
            selectedProviderTypes={selectedProviderTypes}
            mode={radarMode}
            focusLabel={activePracticeArea ? activePracticeArea.name : ""}
            onZipSelect={(zipData) => {
              setSelectedZipData(zipData);
              // Clicking a ZIP also runs the provider search for it
              if (zipData?.zip) {
                setSearchZip(String(zipData.zip));
                searchProviders(String(zipData.zip));
              }
            }}
          />
        </div>
      </div>

      {/* Market Entry Plan */}
      {activePracticeArea && (
        <div className="mb-6 p-4 bg-white rounded-lg shadow border border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                Market Entry Plan — {activePracticeArea.name}
              </h3>
              <p className="text-sm text-gray-500">
                Untapped referrers across the whole area, ranked by private-pay volume, activity, and quality.
              </p>
            </div>
            <button
              onClick={buildEntryPlan}
              disabled={entryPlanLoading}
              className="px-4 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {entryPlanLoading ? "Building..." : entryPlan ? "Rebuild Entry Plan" : "Build Entry Plan"}
            </button>
          </div>

          {entryPlanError && (
            <p className="text-sm text-red-600 mb-2">{entryPlanError}</p>
          )}

          {entryPlan && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 text-center">
                <div className="p-2 bg-gray-50 rounded">
                  <p className="text-xl font-bold text-gray-800">{entryPlan.summary.total_referrers}</p>
                  <p className="text-xs text-gray-500">Referrers in area</p>
                </div>
                <div className="p-2 bg-red-50 rounded">
                  <p className="text-xl font-bold text-red-700">{entryPlan.summary.untapped_referrers}</p>
                  <p className="text-xs text-gray-500">Untapped</p>
                </div>
                <div className="p-2 bg-green-50 rounded">
                  <p className="text-xl font-bold text-green-700">{entryPlan.summary.matched_referrers}</p>
                  <p className="text-xs text-gray-500">Already referring</p>
                </div>
                <div className="p-2 bg-purple-50 rounded">
                  <p className="text-xl font-bold text-purple-700">{entryPlan.summary.penetration_rate}%</p>
                  <p className="text-xs text-gray-500">Penetrance</p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase border-b">
                      <th className="py-2 pr-2"></th>
                      <th className="py-2 pr-3">#</th>
                      <th className="py-2 pr-3">Provider</th>
                      <th className="py-2 pr-3">Specialty</th>
                      <th className="py-2 pr-3">ZIP</th>
                      <th className="py-2 pr-3">Tier</th>
                      <th className="py-2 pr-3">Payer Mix</th>
                      <th className="py-2 pr-3 text-right" title="Blend of payer mix, market activity, and provider quality — higher = better first target">Priority Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entryPlan.targets.map((target) => (
                      <tr key={target.referrer_id} className="border-b hover:bg-gray-50">
                        <td className="py-2 pr-2">
                          <input
                            type="checkbox"
                            checked={entryPlanSelected.includes(target.referrer_id)}
                            onChange={() => toggleEntryTarget(target.referrer_id)}
                          />
                        </td>
                        <td className="py-2 pr-3 text-gray-500">{target.rank}</td>
                        <td className="py-2 pr-3 font-medium text-gray-800">{target.name}</td>
                        <td className="py-2 pr-3 text-gray-600">{target.specialty}</td>
                        <td className="py-2 pr-3 text-gray-600">{target.zip_code}</td>
                        <td className="py-2 pr-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            target.activity_tier === "Top Referrer"
                              ? "bg-purple-100 text-purple-800"
                              : target.activity_tier === "High Activity"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-600"
                          }`}>
                            {target.activity_tier}
                          </span>
                        </td>
                        <td className="py-2 pr-3">{payerBadge(target)}</td>
                        <td className="py-2 pr-3 text-right font-semibold text-gray-800" title="Blend of payer mix, market activity, and provider quality">{target.entry_score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  onClick={createOutreachFromEntryPlan}
                  disabled={entryPlanSelected.length === 0 || !!entryPlanCreated}
                  className="px-4 py-2 rounded bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  Create Outreach Plan ({entryPlanSelected.length} targets)
                </button>
                {entryPlanCreated && (
                  <span className="text-sm text-green-700">
                    ✅ Created "{entryPlanCreated.name}" with {entryPlanCreated.target_count} targets — see the Outreach tab.
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ZIP Details Card */}
      {selectedZipData && selectedZipData.market_category !== "no_data" && (
        <div className="mb-6 p-4 bg-white rounded-lg shadow-lg border-2 border-blue-500">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-semibold text-blue-800">
              ZIP Code {selectedZipData.zip} Analysis
            </h4>
            <button
              onClick={() => setSelectedZipData(null)}
              className="text-gray-500 hover:text-gray-700 text-xl font-bold focus:outline-none bg-transparent border-none p-1"
            >
              ×
            </button>
          </div>

          {/* Market Context Section */}
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <h5 className="font-semibold text-blue-900 mb-3 text-sm">Market Context</h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div title="Share of residents with private health insurance (Census ACS)">
                <div className="text-gray-600 font-medium">Privately Insured</div>
                <div className="text-lg font-bold text-blue-800">
                  {selectedZipData.private_coverage_rate != null
                    ? `${Math.round(selectedZipData.private_coverage_rate)}%`
                    : '—'}
                </div>
                {selectedZipData.uninsured_rate != null && (
                  <div className="text-[11px] text-gray-500">{Math.round(selectedZipData.uninsured_rate)}% uninsured</div>
                )}
              </div>
              <div title="Median household income (Census ACS)">
                <div className="text-gray-600 font-medium">Median Income</div>
                <div className="text-lg font-bold text-blue-800">
                  {selectedZipData.median_household_income != null
                    ? `$${(selectedZipData.median_household_income / 1000).toFixed(0)}k`
                    : '—'}
                </div>
              </div>
              <div>
                <div className="text-gray-600 font-medium">Population</div>
                <div className="text-lg font-bold text-blue-800">
                  {selectedZipData.population != null
                    ? selectedZipData.population.toLocaleString()
                    : '—'}
                </div>
                {selectedZipData.pct_65_plus != null && (
                  <div className="text-[11px] text-gray-500">{Math.round(selectedZipData.pct_65_plus)}% age 65+</div>
                )}
              </div>
              <div title="Providers officed in this ZIP per 100,000 residents (of the selected types, when a specialty filter is on)">
                <div className="text-gray-600 font-medium">Provider Density</div>
                <div className="text-lg font-bold text-blue-800">
                  {selectedZipData.specialist_density_per_100k != null
                    ? `${selectedZipData.specialist_density_per_100k.toLocaleString()} / 100k`
                    : '—'}
                </div>
                {selectedZipData.referrer_count != null && selectedZipData.population != null && (
                  <div className="text-[11px] text-gray-500">
                    {selectedZipData.referrer_count.toLocaleString()} providers · {selectedZipData.population.toLocaleString()} residents
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Your Performance Section */}
          <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
            <h5 className="font-semibold text-green-900 mb-3 text-sm">Your Performance in ZIP {selectedZipData.zip}</h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="text-gray-600 font-medium">Patient Count</div>
                <div className="text-lg font-bold text-green-800">
                  {selectedZipData.patient_count || 0}
                </div>
              </div>
              <div>
                <div className="text-gray-600 font-medium">Avg Referral Quality</div>
                <div className="text-lg font-bold text-green-800">
                  {selectedZipData.avg_referral_quality != null
                    ? `${selectedZipData.avg_referral_quality.toFixed(1)}/5`
                    : '—'}
                </div>
              </div>
              <div>
                <div className="text-gray-600 font-medium">Avg Value Score</div>
                <div className="text-lg font-bold text-green-800">
                  {selectedZipData.avg_referral_value_score != null
                    ? selectedZipData.avg_referral_value_score.toFixed(0)
                    : '—'}
                </div>
              </div>
              <div>
                <div className="text-gray-600 font-medium">Private Insurance Share</div>
                <div className="text-lg font-bold text-green-800">
                  {selectedZipData.private_insurance_rate != null
                    ? `${selectedZipData.private_insurance_rate.toFixed(0)}%`
                    : '—'}
                </div>
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-blue-500">
              <div className="font-medium text-gray-600">Total Referrers</div>
              <div className="text-2xl font-bold text-blue-600">
                {selectedZipData.referrer_count || 0}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-orange-500">
              <div className="font-medium text-gray-600">Untapped Referrers</div>
              <div className="text-2xl font-bold text-orange-600">
                {selectedZipData.untapped_referrers || 0}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-purple-500">
              <div className="font-medium text-gray-600">Opportunity Score</div>
              <div className="text-2xl font-bold text-purple-600">
                {selectedZipData.opportunity_score || 0}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-gray-500">
              <div className="font-medium text-gray-600">Market Category</div>
              <div className="text-lg font-bold text-gray-700 capitalize">
                {selectedZipData.market_category || '—'}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedZipData && (
        <div className="mb-6 p-4 bg-white rounded-lg shadow-lg border border-gray-200">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">
                ZIP {selectedZipData.zip} Referrer Roster
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                Rank providers by specialty, activity, and whether they already refer to you.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 min-w-[280px]">
              <div className="bg-slate-50 p-3 rounded border">
                <div className="text-xs text-gray-500">Potential Referrers</div>
                <div className="text-2xl font-bold text-slate-800">
                  {zipInsights?.provider_count || 0}
                </div>
              </div>
              <div className="bg-amber-50 p-3 rounded border border-amber-100">
                <div className="text-xs text-amber-700">Top Referrers</div>
                <div className="text-2xl font-bold text-amber-800">
                  {zipInsights?.whale_count || 0}
                </div>
              </div>
              <div className="bg-green-50 p-3 rounded border border-green-100">
                <div className="text-xs text-green-700">Already Referring</div>
                <div className="text-2xl font-bold text-green-800">
                  {zipInsights?.already_referring_count || 0}
                </div>
              </div>
              <div className="bg-blue-50 p-3 rounded border border-blue-100">
                <div className="text-xs text-blue-700">Untapped</div>
                <div className="text-2xl font-bold text-blue-800">
                  {zipInsights?.untapped_count || 0}
                </div>
              </div>
            </div>
          </div>

          {zipInsights?.priority_targets?.length > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <h5 className="font-semibold text-amber-900 mb-2">
                Priority Targets
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {zipInsights.priority_targets.slice(0, 3).map((provider) => (
                  <div key={provider.id} className="bg-white rounded border p-3">
                    <div className="font-medium text-gray-900">{provider.name}</div>
                    <div className="text-sm text-gray-600">{provider.specialty || "Unknown"}</div>
                    <div className="mt-2 text-sm">
                      <span className="font-semibold text-amber-700">
                        {provider.activity_tier}
                      </span>
                      <span className="text-gray-500 ml-2">
                        Score {provider.activity_score}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {zipInsights?.payer_mix && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="font-medium text-emerald-900 text-sm">Likely Payer Split in This ZIP (residents, Census ACS)</h4>
                <span className="text-xs font-semibold text-emerald-800">
                  Commercial opportunity: {zipInsights.payer_mix.commercial_proxy}%
                </span>
              </div>
              <div className="mt-2 flex h-3 rounded-full overflow-hidden border border-emerald-200" title="Coverage types overlap (some residents hold both)">
                <div style={{ width: `${Math.min(100, zipInsights.payer_mix.commercial_proxy)}%` }} className="bg-emerald-500" />
                <div style={{ width: `${Math.min(100 - Math.min(100, zipInsights.payer_mix.commercial_proxy), zipInsights.payer_mix.public_coverage_rate || 0)}%` }} className="bg-amber-400" />
                <div className="flex-1 bg-gray-300" />
              </div>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-700">
                <span>Private coverage: <b>{zipInsights.payer_mix.private_coverage_rate}%</b></span>
                <span>Public (Mcare/Mcaid): <b>{zipInsights.payer_mix.public_coverage_rate}%</b></span>
                <span>Uninsured: <b>{zipInsights.payer_mix.uninsured_rate}%</b></span>
                <span>Age 65+: <b>{zipInsights.payer_mix.pct_65_plus}%</b></span>
              </div>
              <p className="mt-1 text-[11px] text-gray-500">
                "Commercial opportunity" discounts private coverage by the senior share (their private plans mostly ride on Medicare).
              </p>
            </div>
          )}

          {(() => {
            const orgs = {};
            (zipInsights?.providers || []).forEach((p) => {
              if (!p.organization_name) return;
              const key = p.org_pac_id || p.organization_name;
              if (!orgs[key]) orgs[key] = { name: p.organization_name, total: 0, active: 0, members: p.org_member_count };
              orgs[key].total += 1;
              if (p.link_status === "active") orgs[key].active += 1;
            });
            const top = Object.values(orgs).sort((a, b) => b.total - a.total).slice(0, 6);
            return top.length > 1 ? (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 text-sm mb-2">
                  Top Institutions in This ZIP
                  <span className="ml-2 text-xs font-normal text-blue-700">win the group, win its referrers</span>
                </h4>
                <div className="flex flex-wrap gap-2">
                  {top.map((o) => (
                    <div key={o.name} className="px-2.5 py-1.5 bg-white border border-blue-200 rounded-lg text-xs">
                      <span className="font-semibold text-gray-800">{o.name}</span>
                      <span className="text-gray-500"> · {o.total} here{o.members ? ` of ${o.members}` : ""}</span>
                      <span className={o.active > 0 ? "text-emerald-700 font-medium" : "text-gray-400"}>
                        {" "}· {o.active} refer to you
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;
          })()}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Relationship</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={zipRelationshipFilter}
                onChange={(e) => setZipRelationshipFilter(e.target.value)}
              >
                <option value="all">All Providers</option>
                <option value="referring">Already Referring</option>
                <option value="untapped">Untapped</option>
                <option value="whales">Top Referrers Only</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Specialty</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={zipSpecialtyFilter}
                onChange={(e) => setZipSpecialtyFilter(e.target.value)}
              >
                <option value="all">All Specialties</option>
                {(zipInsights?.specialties || []).map((specialty) => (
                  <option key={specialty.specialty} value={specialty.specialty}>
                    {specialty.specialty} ({specialty.count})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sort By</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={zipSortBy}
                onChange={(e) => setZipSortBy(e.target.value)}
              >
                <option value="best_targets">Busy + Commercial (best targets)</option>
                <option value="activity">Activity</option>
                <option value="private_payer">Private Payer Likelihood</option>
                <option value="your_referrals">My Relationship</option>
                <option value="specialty">Specialty</option>
                <option value="name">Name</option>
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={zipCommercialOnly}
                  onChange={(e) => setZipCommercialOnly(e.target.checked)}
                />
                Commercial-leaning providers only
              </label>
            </div>
          </div>

          {zipInsightsLoading && (
            <div className="p-4 bg-gray-50 border rounded text-gray-600">
              Loading ZIP insight data...
            </div>
          )}

          {zipInsightsError && !zipInsightsLoading && (
            <div className="p-4 bg-red-50 border border-red-200 rounded text-red-700">
              {zipInsightsError}
            </div>
          )}

          {!zipInsightsLoading && !zipInsightsError && (
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border px-3 py-2 text-left">Provider</th>
                    <th className="border px-3 py-2 text-left">Specialty</th>
                    <th className="border px-3 py-2 text-left">Activity</th>
                    <th className="border px-3 py-2 text-left">Payer Mix</th>
                    <th className="border px-3 py-2 text-left">Market Volume</th>
                    <th className="border px-3 py-2 text-left">Relationship</th>
                    <th className="border px-3 py-2 text-left">Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredZipProviders.map((provider) => (
                    <tr key={provider.id} className="hover:bg-gray-50 align-top">
                      <td className="border px-3 py-2">
                        <div className="font-medium text-gray-900">{provider.name}</div>
                        <div className="text-xs text-gray-500">
                          {provider.city}, {provider.state} {provider.zip_code}
                        </div>
                        {provider.organization_name && (
                          <div className="text-[11px] text-gray-400">🏥 {provider.organization_name}</div>
                        )}
                      </td>
                      <td className="border px-3 py-2">{provider.specialty || "Unknown"}</td>
                      <td className="border px-3 py-2">
                        <div className="font-semibold text-gray-900">
                          {provider.activity_score}
                        </div>
                        <div className="text-xs text-gray-500">{provider.activity_tier}</div>
                      </td>
                      <td className="border px-3 py-2">{payerBadge(provider)}</td>
                      <td className="border px-3 py-2">
                        <div className="text-sm text-gray-900">
                          12m: {provider.market_referral_volume_12m || 0}
                        </div>
                        <div className="text-xs text-gray-500">
                          6m: {provider.market_referral_volume_6m || 0}
                        </div>
                      </td>
                      <td className="border px-3 py-2">
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            provider.relationship_status === "Referring to you"
                              ? "bg-green-100 text-green-800"
                              : provider.relationship_status === "Prospect"
                              ? "bg-blue-100 text-blue-800"
                              : provider.relationship_status === "Dormant"
                              ? "bg-gray-100 text-gray-600"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {provider.relationship_status}
                        </span>
                        {provider.link_status == null && (
                          <div className="mt-1 flex gap-1">
                            <button
                              onClick={() => logReferrer(provider.id, "active")}
                              className="text-[11px] bg-emerald-600 text-white rounded px-1.5 py-0.5 hover:bg-emerald-700"
                              title="They already send you referrals"
                            >
                              Refers to me
                            </button>
                            <button
                              onClick={() => logReferrer(provider.id, "prospect")}
                              className="text-[11px] bg-blue-600 text-white rounded px-1.5 py-0.5 hover:bg-blue-700"
                              title="Add to your prospect list"
                            >
                              + Prospect
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="border px-3 py-2 text-sm">
                        <div>{formatPhone(provider.phone) || "—"}</div>
                        {provider.email ? (
                          <a
                            href={`mailto:${provider.email}`}
                            className="text-blue-600 hover:underline"
                          >
                            {provider.email}
                          </a>
                        ) : (
                          <div className="text-gray-400">No email</div>
                        )}
                        <button
                          onClick={() => openOutreachModal({ referrerId: provider.id, npi: provider.npi, name: provider.name })}
                          className="mt-1 text-xs bg-green-600 text-white border border-green-700 rounded px-2 py-0.5 hover:bg-green-700 font-medium"
                        >
                          + Outreach
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filteredZipProviders.length === 0 && (
                    <tr>
                      <td colSpan={7} className="border px-3 py-6 text-center text-gray-500">
                        No providers match the current filters for this ZIP.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Provider Search */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-3">Search for Providers by ZIP Code</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              ZIP Code(s) - Comma separated for multiple
            </label>
            <input
              type="text"
              placeholder="e.g., 46033, 46032, 46074"
              value={searchZip}
              onChange={(e) => setSearchZip(e.target.value)}
              className="border rounded px-3 py-2 w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter one or more ZIP codes separated by commas
            </p>
          </div>
          <div className="flex items-end">
            <button
              onClick={searchProviders}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded w-full disabled:opacity-50"
            >
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {referrers.length > 0 && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h3 className="text-lg font-semibold">
              Search Results ({referrers.length.toLocaleString()} providers found)
            </h3>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              Sort by
              <select
                value={searchSort}
                onChange={(e) => { setSearchSort(e.target.value); setCurrentPage(1); }}
                className="border rounded px-2 py-1 text-sm"
              >
                <option value="activity">Total activity</option>
                <option value="private_payer">Private payer likelihood</option>
                <option value="location">Location (city, ZIP)</option>
                <option value="specialty">Specialty</option>
                <option value="name">Name</option>
              </select>
            </label>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border border-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-2 py-1">Name</th>
                  <th className="border px-2 py-1">Specialty</th>
                  <th className="border px-2 py-1">Activity</th>
                  <th className="border px-2 py-1">Payer Mix</th>
                  <th className="border px-2 py-1">Est. Referrals/yr</th>
                  <th className="border px-2 py-1">City</th>
                  <th className="border px-2 py-1">State</th>
                  <th className="border px-2 py-1">ZIP</th>
                  <th className="border px-2 py-1">Phone</th>
                  <th className="border px-2 py-1">Email</th>
                  <th className="border px-2 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {currentReferrers.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="border px-2 py-1">{r.name}</td>
                    <td className="border px-2 py-1">{r.specialty}</td>
                    <td className="border px-2 py-1">
                      {r.activity_tier ? (
                        <span className={`inline-flex items-center gap-1 text-xs font-medium border rounded-full px-2 py-0.5 ${tierBadgeClass(r.activity_tier)}`}>
                          {r.activity_tier}
                          {r.activity_score != null && <span className="opacity-70">({Math.round(r.activity_score)})</span>}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400" title="Not yet tracked — no local activity data for this provider">No data</span>
                      )}
                    </td>
                    <td className="border px-2 py-1">{payerBadge(r)}</td>
                    <td className="border px-2 py-1 text-center">
                      {r.market_referral_volume_12m != null && r.market_referral_volume_12m > 0
                        ? r.market_referral_volume_12m
                        : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="border px-2 py-1">{r.city}</td>
                    <td className="border px-2 py-1">{r.state}</td>
                    <td className="border px-2 py-1">{r.zip_code}</td>
                    <td className="border px-2 py-1">{formatPhone(r.phone)}</td>
                    <td className="border px-2 py-1">
                      {r.email ? (
                        <a href={`mailto:${r.email}`} className="text-blue-600 hover:underline">
                          {r.email}
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="border px-2 py-1">
                      <button
                        onClick={() => openOutreachModal({ referrerId: r.referrer_id, npi: r.npi, name: r.name })}
                        className="text-xs bg-green-600 text-white border border-green-700 rounded px-2 py-0.5 hover:bg-green-700 font-medium"
                      >
                        + Outreach
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-center mt-4 space-x-2">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 border rounded ${
                  currentPage === i + 1
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <p className="text-center mt-2 text-sm text-gray-600">
            Showing {currentReferrers.length} of {filteredReferrers.length} providers{activePracticeArea ? ` (filtered by ${activePracticeArea.name})` : ""}
          </p>
        </>
      )}

      {referrers.length === 0 && !loading && (
        <div className="text-center text-gray-500 py-8">
          <p>No providers found.</p>
          <p className="text-sm mt-2">
            Try searching with a ZIP code, selecting different states, or adjusting provider type filters.
          </p>
        </div>
      )}

      {/* Outreach Modal */}
      {outreachModal && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "1.5rem", width: "360px", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}>
            <h3 style={{ margin: "0 0 1rem", fontSize: "1.1rem", fontWeight: 700, color: "#15803d" }}>Add to Outreach Plan</h3>
            {outreachModal.name && (
              <p style={{ margin: "0 0 0.75rem", color: "#4b5563", fontSize: "0.9rem" }}>
                {outreachModal.name}
              </p>
            )}
            <label style={{ display: "block", fontSize: "0.85rem", marginBottom: "0.25rem", fontWeight: 600 }}>Plan</label>
            <select value={outreachPlanId} onChange={e => setOutreachPlanId(e.target.value)} style={{ width: "100%", padding: "0.5rem", borderRadius: "6px", border: "1px solid #d1d5db", marginBottom: "0.75rem" }}>
              <option value="">Select a plan…</option>
              {outreachPlans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {outreachPlans.length === 0 && (
              <p style={{ margin: "0 0 0.75rem", color: "#b45309", fontSize: "0.85rem" }}>
                Create an outreach plan first on the Outreach page.
              </p>
            )}
            <label style={{ display: "block", fontSize: "0.85rem", marginBottom: "0.25rem", fontWeight: 600 }}>Category</label>
            <select value={outreachCategory} onChange={e => setOutreachCategory(e.target.value)} style={{ width: "100%", padding: "0.5rem", borderRadius: "6px", border: "1px solid #d1d5db", marginBottom: "1rem" }}>
              <option value="expand">Expand To</option>
              <option value="bank">Bank / Retain</option>
            </select>
            {outreachError && (
              <div style={{ marginBottom: "0.75rem", padding: "0.75rem", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", color: "#b91c1c", fontSize: "0.85rem" }}>
                {outreachError}
              </div>
            )}
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
              <button onClick={closeOutreachModal} style={{ padding: "0.5rem 1rem", borderRadius: "6px", border: "1px solid #d1d5db", backgroundColor: "white", color: "#374151", cursor: "pointer" }}>Cancel</button>
              <button onClick={addToOutreach} disabled={!outreachPlanId || outreachPlans.length === 0 || outreachSubmitting} style={{ padding: "0.5rem 1rem", borderRadius: "6px", border: "none", backgroundColor: "#22c55e", color: "white", fontWeight: 600, cursor: "pointer", opacity: outreachPlanId && outreachPlans.length > 0 && !outreachSubmitting ? 1 : 0.5 }}>
                {outreachSubmitting ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
